import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CircleAlert, Loader2, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { queryKeys } from "@/lib/query/keys";
import { AuthScreen } from "@/routes/auth-screen";

import { answerTransfer, verifyEnrolmentLink, type EnrolmentSession } from "./api";

type State =
  | { kind: "verifying" }
  | { kind: "spent" }
  | { kind: "transfer"; session: EnrolmentSession }
  | { kind: "working" };

/**
 * Consumes the emailed verification link, then either sends the participant on
 * to their profile form or offers the transfer.
 *
 * **The transfer offer lives here, behind the link, and not on the public
 * page.** That is the whole placement decision of this change: on the form,
 * anyone who knew a participant's address and held any open code could erase
 * their work. Here, the person answering has just proved they can read the
 * mailbox.
 */
export function EnrolmentVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { payload = "" } = useParams({ strict: false }) as { payload?: string };
  const [state, setState] = useState<State>({ kind: "verifying" });

  // The link is single-use: React 18 StrictMode mounts effects twice in dev,
  // and a second call would spend a link that the first one had just consumed.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current || !payload) return;
    consumed.current = true;

    verifyEnrolmentLink(payload)
      .then(async (session) => {
        // Whatever the cache holds belongs to whoever was signed in before.
        // This is a fresh document load, so `hasSession()` has already seen the
        // previous user's refresh token in storage and `meQuery` may have
        // resolved as them — before this link's pair was stored — and `/me` is
        // cached with `staleTime: Infinity`. `login()` invalidates for exactly
        // this reason; the enrolment journey cannot call it, there being no
        // password. Without this a shared browser keeps the previous person's
        // name and data on screen while every request carries the new token.
        //
        // `clear()` is what shipped here first, and it left the bug open: it
        // removes the cached `/me` *and* detaches its observer, so nothing
        // refetches and the next route reads `undefined`. Invalidating instead
        // refetches the observer `AuthProvider` already has mounted, and the
        // **await** is the load-bearing part — `EnterpriseProfilePage`
        // snapshots `enterprise_profile_id` once at mount and never re-reads
        // it, so `/me` has to be the participant before we navigate, not
        // eventually.
        await qc.invalidateQueries({ queryKey: queryKeys.auth.me() });
        // Everything else in the cache is the previous person's data under a
        // session that is now someone else's. Dropped after `/me`, so this
        // cannot detach the observer the line above depends on.
        qc.removeQueries({
          predicate: (query) => query.queryKey[0] !== queryKeys.auth.all[0],
        });
        // The other half of this fix is in `refreshAccessToken`: until now the
        // previous user's in-flight refresh could overwrite the token pair
        // this link had just stored, leaving them signed in.
        if (session.transfer_ticket) {
          setState({ kind: "transfer", session });
        } else {
          void navigate({ to: "/enterprise-profile" });
        }
      })
      .catch(() => setState({ kind: "spent" }));
  }, [payload, navigate, qc]);

  async function answer(confirm: boolean) {
    if (state.kind !== "transfer") return;
    const ticket = state.session.transfer_ticket;
    if (!ticket) return;

    setState({ kind: "working" });
    try {
      await answerTransfer({ confirm, ticket });
      if (confirm) {
        // Django flashes the same sentence here; refusing is silent in both.
        toast.success(
          t("enrolment.transferDone", { group: state.session.transfer_to_name }),
        );
      }
    } catch {
      // A failed answer leaves the participant where they were, which is the
      // safe side of this choice. `catch` rather than `finally` so the
      // rejection does not escape an async handler that cannot report it.
    }
    // Answering moved the participant to another formation group, and `/me`
    // is cached at `staleTime: Infinity` — so without this the next screen
    // reads the group they were in before they answered. Invalidated after
    // the answer rather than before it, and awaited, for the same reason the
    // direct branch above awaits: the next route reads `/me` at mount.
    await qc.invalidateQueries({ queryKey: queryKeys.auth.me() });
    void navigate({ to: "/enterprise-profile" });
  }

  if (state.kind === "spent") {
    return (
      <AuthScreen>
        <div className="text-center">
          <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-destructive/10">
            <CircleAlert className="size-6 text-destructive-ink" aria-hidden />
          </div>
          <h1 className="text-2xl font-extrabold">{t("enrolment.spentTitle")}</h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("enrolment.spentBody")}
          </p>
          <Button
            className="mt-5 w-full"
            onClick={() => void navigate({ to: "/enrolment" })}
          >
            {t("enrolment.spentAction")}
          </Button>
        </div>
      </AuthScreen>
    );
  }

  if (state.kind === "transfer") {
    const { transfer_to_name: target, transfer_from_name: current } = state.session;
    return (
      <AuthScreen>
        <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-warning/10">
          <TriangleAlert className="size-6 text-warning-ink" aria-hidden />
        </div>
        <h1 className="text-2xl font-extrabold">{t("enrolment.transferTitle")}</h1>
        <p className="mt-1.5 text-[14.5px] text-muted-foreground">
          {current
            ? t("enrolment.transferBody", { current, target })
            : t("enrolment.transferBodyNoGroup", { target })}
        </p>
        <p className="mt-3 text-[13.5px] text-muted-foreground">
          {t("enrolment.transferWhy")}
        </p>
        <Button
          variant="outline"
          className="mt-5 w-full"
          onClick={() => void answer(false)}
        >
          {t("enrolment.transferRefuse")}
        </Button>
        <Button
          variant="destructive"
          className="mt-3 w-full"
          onClick={() => void answer(true)}
        >
          {t("enrolment.transferConfirm")}
        </Button>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" aria-hidden />
        {t("enrolment.verifying")}
      </div>
    </AuthScreen>
  );
}
